import { ChevronDownIcon, SchoolIcon, LayerIcon } from '@bi-digital/icons';
import {
    Badge,
    Checkbox,
    Dropdown,
    DropdownItem,
    DropdownList,
    DropdownTrigger,
    DropdownTriggerContext,
    FilterGroup,
    Radio,
} from '@bi-digital/react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './filters.module.css';

export type FiltersProps = {
    levels: string[];
    academicDegrees: string[];
    level: string;
    selectedDegrees: string[];
    setLevel: (level: string) => void;
    setSelectedDegrees: (degrees: string[]) => void;
};

const useFilterOptions = (props: FiltersProps) => {
    const { t } = useTranslation('former-students');

    const toggleDegree = (degree: string, checked: boolean) =>
        props.setSelectedDegrees(
            checked ? [...props.selectedDegrees, degree] : props.selectedDegrees.filter((d) => d !== degree),
        );

    const levelOptions = ['', ...props.levels].map((level) => (
        <Radio
            key={level}
            name='level'
            value={level}
            checked={props.level === level}
            onChange={() => props.setLevel(level)}
            label={level ? t(`levels.${level}`) : t('all')}
            fullwidth
        />
    ));

    const degreeOptions = props.academicDegrees.map((degree) => (
        <Checkbox
            key={degree}
            value={degree}
            checked={props.selectedDegrees.includes(degree)}
            onChange={(e) => toggleDegree(degree, e.target.checked)}
            label={degree}
            fullwidth
        />
    ));

    return { t, levelOptions, degreeOptions };
};

export const FilterGroups = (props: FiltersProps) => {
    const { t, levelOptions, degreeOptions } = useFilterOptions(props);

    return (
        <div>
            {props.levels.length > 0 && (
                <FilterGroup icon={<LayerIcon aria-hidden='true' />} label={t('level')} defaultOpen>
                    {levelOptions}
                </FilterGroup>
            )}
            {props.academicDegrees.length > 0 && (
                <FilterGroup icon={<SchoolIcon aria-hidden='true' />} label={t('academicDegree')} defaultOpen>
                    {degreeOptions}
                </FilterGroup>
            )}
        </div>
    );
};

type FilterDropdownProps = {
    icon: ReactElement;
    label: string;
    selectedCount: number;
    children: ReactElement[];
};

const FilterDropdown = ({ icon, label, selectedCount, children }: FilterDropdownProps) => (
    <DropdownTriggerContext>
        <DropdownTrigger
            className={styles.trigger}
            size='large'
            fullwidth
            startIcon={icon}
            endIcon={<ChevronDownIcon aria-hidden='true' />}
        >
            <span className={styles.triggerLabel}>{label}</span>
            {selectedCount > 0 && <Badge size='small'>{selectedCount}</Badge>}
        </DropdownTrigger>
        <Dropdown className={styles.panel} placement='bottom-start'>
            <DropdownList>
                {children.map((option) => (
                    <DropdownItem key={option.key}>{option}</DropdownItem>
                ))}
            </DropdownList>
        </Dropdown>
    </DropdownTriggerContext>
);

export const FilterDropdowns = (props: FiltersProps) => {
    const { t, levelOptions, degreeOptions } = useFilterOptions(props);

    return (
        <div className={styles.dropdowns}>
            {props.levels.length > 0 && (
                <FilterDropdown
                    icon={<LayerIcon aria-hidden='true' />}
                    label={t('level')}
                    selectedCount={props.level ? 1 : 0}
                >
                    {levelOptions}
                </FilterDropdown>
            )}
            {props.academicDegrees.length > 0 && (
                <FilterDropdown
                    icon={<SchoolIcon aria-hidden='true' />}
                    label={t('academicDegree')}
                    selectedCount={props.selectedDegrees.length}
                >
                    {degreeOptions}
                </FilterDropdown>
            )}
        </div>
    );
};
